import { motion } from "framer-motion";
import { Check, Zap } from "lucide-react";
import { EASE } from "./shared";
import { useLang } from "./i18n";

const STATUSES = ["done", "done", "active", "pending", "pending"];

export function DashboardCard({ start = true }) {
  const { t } = useLang();
  return (
    <div
      data-testid="dashboard-card"
      className="relative rounded-3xl bg-white p-6 text-left shadow-[0_36px_90px_rgba(0,0,0,0.5)] sm:p-7"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="pulse-dot absolute h-full w-full rounded-full bg-aqua-500" />
            <span className="h-2.5 w-2.5 rounded-full bg-aqua-500" />
          </span>
          <span className="text-sm font-bold text-ink">{t.dash.live}</span>
        </div>
        <span className="rounded-lg bg-slate-100 px-2.5 py-1 font-mono text-[11px] font-semibold text-slate-500">
          STL-2026-0847
        </span>
      </div>

      <div className="mt-5 border-b border-slate-100 pb-5">
        <div className="font-display text-xl font-semibold text-ink">{t.dash.property}</div>
        <div className="mt-0.5 text-xs font-medium text-slate-400">{t.dash.price}</div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between text-xs font-semibold">
          <span className="text-slate-500">{t.dash.progress}</span>
          <span className="text-aqua-700">68%</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
          <motion.div
            initial={{ width: 0 }}
            animate={start ? { width: "68%" } : {}}
            transition={{ delay: 0.9, duration: 1.4, ease: EASE }}
            className="h-full rounded-full bg-gradient-to-r from-aqua-600 to-aqua-400"
          />
        </div>
      </div>

      <div className="mt-6 space-y-1">
        {t.dash.steps.map((step, i) => {
          const status = STATUSES[i];
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -16 }}
              animate={start ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 1.0 + i * 0.12, duration: 0.5, ease: EASE }}
              className="flex items-center gap-3 rounded-xl px-2 py-2"
            >
              {status === "done" && (
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-aqua-600">
                  <Check size={13} strokeWidth={3} className="text-white" />
                </span>
              )}
              {status === "active" && (
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-maple">
                  <span className="pulse-dot h-2 w-2 rounded-full bg-maple" />
                </span>
              )}
              {status === "pending" && (
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 text-[10px] font-bold text-slate-400">
                  {i + 1}
                </span>
              )}
              <span
                className={`flex-1 text-sm ${
                  status === "active" ? "font-bold text-ink" : status === "done" ? "font-semibold text-slate-600" : "font-medium text-slate-400"
                }`}
              >
                {step.label}
              </span>
              <span className={`text-[11px] font-semibold ${status === "active" ? "text-maple" : "text-slate-400"}`}>
                {step.date}
              </span>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={start ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 1.6, duration: 0.5, ease: EASE }}
        className="mt-5 flex items-center gap-3 rounded-2xl border border-maple/30 bg-maple/10 px-4 py-3"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-maple">
          <Zap size={15} className="fill-white text-white" />
        </span>
        <div>
          <div className="text-xs font-bold text-ink">{t.dash.aiTitle}</div>
          <div className="text-xs text-slate-500">{t.dash.aiMsg}</div>
        </div>
      </motion.div>
    </div>
  );
}
